import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { Briefcase, Users, MapPin } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type {
  ICheckInContext,
  ICheckInContextLocation,
} from "@/types/(company)/attendanceRulesV2.interface";

interface Props {
  open: boolean;
  context: ICheckInContext | null;
  onCancel: () => void;
  /**
   * Called on each selection step.
   *  - SCOPE step → returns { teamId } or { projectId }
   *  - LOCATION step → returns { locationId } (plus the prior scope ids)
   * Parent advances the flow by re-fetching context as needed.
   */
  onConfirm: (selection: {
    teamId?: number;
    projectId?: number;
    locationId?: number;
  }) => void;
}

type ScopeOption = {
  kind: "TEAM" | "PROJECT";
  id: number;
  name: string;
  ruleName: string | null;
};

export const CheckInScopeSelector: React.FC<Props> = ({
  open,
  context,
  onCancel,
  onConfirm,
}) => {
  const { t } = useTranslation(["common", "company", "my-space"]);
  const [scopePick, setScopePick] = useState<ScopeOption | null>(null);
  const [locationPick, setLocationPick] =
    useState<ICheckInContextLocation | null>(null);

  if (!context) return null;

  const isScopeStep = context.selectionType === "SCOPE";
  const isLocationStep = context.selectionType === "LOCATION";

  if (isScopeStep) {
    const options: ScopeOption[] = [
      ...context.projects.map<ScopeOption>((p) => ({
        kind: "PROJECT",
        id: p.id,
        name: p.name,
        ruleName: p.ruleName,
      })),
      ...context.teams.map<ScopeOption>((t) => ({
        kind: "TEAM",
        id: t.id,
        name: t.name,
        ruleName: t.ruleName,
      })),
    ];

    const handleConfirm = () => {
      if (!scopePick) return;
      onConfirm({
        teamId: scopePick.kind === "TEAM" ? scopePick.id : undefined,
        projectId: scopePick.kind === "PROJECT" ? scopePick.id : undefined,
      });
      setScopePick(null);
    };

    return (
      <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("my-space:checkin.select_checkin_scope")}</DialogTitle>
            <DialogDescription>
              {t("my-space:checkin.multiple_rules_pick_one")}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-2 py-2 max-h-[320px] overflow-y-auto">
            {options.map((opt) => {
              const isActive =
                scopePick?.kind === opt.kind && scopePick?.id === opt.id;
              const Icon = opt.kind === "PROJECT" ? Briefcase : Users;
              return (
                <button
                  key={`${opt.kind}-${opt.id}`}
                  type="button"
                  onClick={() => setScopePick(opt)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded border text-start transition-colors ${
                    isActive
                      ? "border-primary bg-primary/5"
                      : "border-border hover:bg-muted/30"
                  }`}
                >
                  <Icon
                    size={18}
                    className={
                      isActive ? "text-primary" : "text-muted-foreground"
                    }
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {opt.name || `${opt.kind} #${opt.id}`}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {opt.kind === "PROJECT"
                        ? t("my-space:ui.project")
                        : t("my-space:ui.team")}
                      {opt.ruleName && ` · ${opt.ruleName}`}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={onCancel}>
              {t("common:buttons.cancel")}
            </Button>
            <Button disabled={!scopePick} onClick={handleConfirm}>
              {t("common:buttons.next")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (isLocationStep) {
    const handleConfirm = () => {
      if (!locationPick) return;
      onConfirm({ locationId: locationPick.id });
      setLocationPick(null);
    };

    return (
      <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("my-space:checkin.select_checkin_location")}</DialogTitle>
            <DialogDescription>
              {t("my-space:checkin.multiple_locations_pick_one")}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-2 py-2 max-h-[320px] overflow-y-auto">
            {context.locations.map((loc) => {
              const isActive = locationPick?.id === loc.id;
              return (
                <button
                  key={loc.id}
                  type="button"
                  onClick={() => setLocationPick(loc)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded border text-start transition-colors ${
                    isActive
                      ? "border-primary bg-primary/5"
                      : "border-border hover:bg-muted/30"
                  }`}
                >
                  <MapPin
                    size={18}
                    className={
                      isActive ? "text-primary" : "text-muted-foreground"
                    }
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {loc.label ||
                        t("my-space:checkin.location_number", { id: loc.id })}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {t("my-space:checkin.radius_and_coordinates", {
                        meters: loc.radius,
                        latitude: Number(loc.latitude).toFixed(4),
                        longitude: Number(loc.longitude).toFixed(4),
                      })}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={onCancel}>
              {t("common:buttons.cancel")}
            </Button>
            <Button disabled={!locationPick} onClick={handleConfirm}>
              {t("my-space:checkin.continue_checkin")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return null;
};
